import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Box, Button, ClickAwayListener, MenuItem, TextField } from '@mui/material';
import { useState } from 'react';

import {
  CREATED_RANGES,
  type CreatedRange,
  formatDate,
  formatShortDate,
} from '~/components/tasks/taskStyle';
import { FILTER_CONTROL_ACTIVE_SX, FILTER_CONTROL_SX, TRANSIENT_SURFACE_RADIUS } from '~/theme';

export type CreatedFilter = { range: CreatedRange; from: string; to: string };

export const ANY_TIME: CreatedFilter = { range: 'any', from: '', to: '' };

const customBound = (value: string) =>
  value.startsWith(String(new Date().getFullYear())) ? formatShortDate(value) : formatDate(value);

function filterLabel({ range, from, to }: CreatedFilter): string {
  if (range === 'custom') {
    return `${from ? customBound(from) : 'Start'} – ${to ? customBound(to) : 'Today'}`;
  }
  return CREATED_RANGES.find((r) => r.key === range)?.label ?? '';
}

export function CreatedDateFilter({
  value,
  onChange,
  countFor,
}: {
  value: CreatedFilter;
  onChange: (next: CreatedFilter) => void;
  countFor: (range: Exclude<CreatedRange, 'custom'>) => number;
}) {
  const [open, setOpen] = useState(false);
  const active = value.range !== 'any';

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative' }}>
        <Button
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          sx={{
            ...FILTER_CONTROL_SX,
            ...(active && FILTER_CONTROL_ACTIVE_SX),
            gap: '6px',
            pl: 1.5,
            pr: 1,
            color: active ? 'primary.main' : 'text.primary',
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          Created:{' '}
          <Box component="b" sx={{ fontWeight: 500 }}>
            {filterLabel(value)}
          </Box>
          <ArrowDropDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Button>
        {open ? (
          <Box
            role="menu"
            sx={{
              position: 'absolute',
              top: 36,
              left: 0,
              zIndex: 20,
              width: 320,
              bgcolor: '#fff',
              border: '1px solid #dfe3e8',
              borderRadius: `${TRANSIENT_SURFACE_RADIUS}px`,
              boxShadow: '0 10px 30px rgba(20,40,90,.14)',
              p: '6px',
            }}
          >
            {CREATED_RANGES.map(({ key, label }) => {
              const selected = value.range === key;
              return (
                <MenuItem
                  key={key}
                  selected={selected}
                  onClick={() => {
                    onChange({ range: key, from: '', to: '' });
                    setOpen(false);
                  }}
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: '6px',
                    fontSize: 13,
                  }}
                >
                  {label}
                  <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {countFor(key)}
                  </Box>
                </MenuItem>
              );
            })}
            <Box
              sx={{
                borderTop: '1px solid #eef0f3',
                mt: '6px',
                p: '10px 6px 4px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Box sx={{ fontSize: 11, color: '#666', letterSpacing: '.04em' }}>CUSTOM RANGE</Box>
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <TextField
                  type="date"
                  size="small"
                  value={value.from}
                  onChange={(e) => onChange({ ...value, from: e.target.value, range: 'custom' })}
                  slotProps={{ htmlInput: { 'aria-label': 'Created from' } }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Box component="span" sx={{ color: '#777', fontSize: 12 }}>
                  to
                </Box>
                <TextField
                  type="date"
                  size="small"
                  value={value.to}
                  onChange={(e) => onChange({ ...value, to: e.target.value, range: 'custom' })}
                  slotProps={{ htmlInput: { 'aria-label': 'Created to' } }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
}
